import { css } from "@emotion/css";
import { ReactNode, useEffect, useMemo, useRef } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { api } from "../api/API";
import "./quillAutoLink";
import { htmlImageMax, useImagePreview } from "./HtmlImage";
import { useSnackAction } from "./SnackAction";
import { useSyncedRef } from "./SyncedRef";

const toolbar = [
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "clean"],
];

// imageTypes are the pasted or dropped files that are stored and shown as images
const imageTypes = ["image/png", "image/jpeg", "image/gif", "image/webp"];

export function HtmlEditor(props: {
    placeholder?: string;
    minHeight?: number;

    // fontSize is the text size in px
    fontSize?: number;

    // actions sit at the right-hand end of the toolbar row, alongside the style buttons
    actions?: ReactNode;

    // inset holds the text off the left and right edges. An editor running the width
    // of a panel passes the panel's own gutter, so its text lines up with the labels
    // above it.
    inset?: number;

    // onSubmit runs on cmd/ctrl+enter, for an editor whose content is posted rather
    // than saved as it's typed
    onSubmit?: () => void;

    autoFocus?: boolean;
    value: string;
    onChange: (value: string) => void;
}) {
    const editor = useRef<ReactQuill>(null);
    const preview = useImagePreview();

    // a pasted or dropped image is stored as a file, and the text points at it rather
    // than holding the image itself. The server links the file when the text is saved.
    const upload = useSnackAction(async (input: { index: number; length: number; files: File[] }) => {
        const quill = editor.current?.getEditor();
        if (!quill) return;

        const urls: string[] = [];
        for (const file of input.files) {
            const stored = await api.files.upload({ file: file });
            urls.push(api.files.downloadUrl({ id: stored.id, inline: true }));
        }

        if (input.length > 0) quill.deleteText(input.index, input.length, "user");
        urls.forEach((url, offset) => quill.insertEmbed(input.index + offset, "image", url, "user"));
        quill.setSelection(input.index + urls.length, 0, "silent");
    }, { success: "Image added", loading: "Uploading image…" });

    const uploadRef = useSyncedRef(upload.callback);

    // quill is set up once, so the handler reaches the upload through a ref
    const modules = useMemo(() => ({
        toolbar: toolbar,
        autoLink: true,
        uploader: {
            mimetypes: imageTypes,
            handler: (range: { index: number; length: number }, files: File[]) =>
                uploadRef.current({ index: range.index, length: range.length, files: files }),
        },
    }), [uploadRef]);

    useEffect(() => {
        if (props.autoFocus) editor.current?.focus();
    }, [props.autoFocus]);

    return (
        <div
            className={editorCls}
            style={{
                ["--editor-min-height" as any]: (props.minHeight || 170) + "px",

                // keep the style buttons clear of whatever sits at the end of the row
                ["--editor-actions-width" as any]: props.actions ? actionsWidth + "px" : "0px",
                ["--editor-inset" as any]: (props.inset || defaultInset) + "px",
                ["--editor-font-size" as any]: (props.fontSize || defaultFontSize) + "px",
            }}
            onClick={preview.onClick}
            onKeyDown={e => {
                if (!props.onSubmit) return;
                if (e.key !== "Enter" || !(e.metaKey || e.ctrlKey)) return;

                // quill leaves this combination alone, so the newline it would
                // otherwise land in the text is the browser's
                e.preventDefault();
                props.onSubmit();
            }}
        >
            <ReactQuill
                ref={editor}
                theme="snow"
                modules={modules}
                placeholder={props.placeholder}
                value={props.value}
                onChange={props.onChange}
            />
            {props.actions && <div className="editor-actions">{props.actions}</div>}
            {preview.element}
        </div>
    );
}

// listIndent is how far a list sits in from the text around it, and indentStep how far
// each level of indenting goes after that. Quill's own 1.5em and 3em walk the text a
// long way off the left edge.
const listIndent = 12;
const indentStep = 24;

// quill marks the levels with .ql-indent-1 through .ql-indent-9, and a list item needs
// room for its own marker on top of whatever level it's at
const indentLevels = Object.fromEntries(
    Array.from({ length: 9 }, (_, index) => index + 1).flatMap(level => [
        [`.ql-editor .ql-indent-${level}:not(.ql-direction-rtl)`,
            { paddingLeft: level * indentStep }],
        [`.ql-editor li.ql-indent-${level}:not(.ql-direction-rtl)`,
            { paddingLeft: `calc(${level * indentStep}px + 1.5em)` }],
    ]));

// actionsWidth is the room the toolbar leaves for the actions, and toolbarHeight the
// row they line up in
const actionsWidth = 150;
const defaultInset = 12;
const defaultFontSize = 15;
const toolbarPadding = 8;

// quillButton is the size of a snow toolbar button, and quillButtonInset the padding
// inside it that the icon sits behind
const quillButton = 24;
const quillButtonInset = 5;
const toolbarHeight = quillButton + toolbarPadding * 2 + 1;

// column-reverse puts quill's toolbar under the text area, and the grays tone the
// controls down so the text stays the focus
const editorCls = css({
    position: "relative",
    ".editor-actions": {
        position: "absolute",
        right: 8,
        bottom: 0,
        height: toolbarHeight,
        display: "flex",
        alignItems: "center",
        gap: 8,
    },
    ".quill": {
        display: "flex",
        flexDirection: "column-reverse",
    },
    ".ql-toolbar.ql-snow": {
        border: "none",
        borderTop: "1px solid #e0e0e0",
        borderBottom: "1px solid #e0e0e0",
        background: "#fafafa",
        padding: toolbarPadding,

        // line the first icon up with the text above it rather than with the edge
        paddingLeft: `calc(var(--editor-inset) - ${quillButtonInset}px)`,
        paddingRight: `calc(${toolbarPadding}px + var(--editor-actions-width))`,
    },
    ".ql-container.ql-snow": {
        border: "none",
        borderTop: "1px solid #e0e0e0",
        fontSize: "var(--editor-font-size)",
    },
    ".ql-editor": {
        minHeight: "var(--editor-min-height)",
        padding: "10px var(--editor-inset)",
    },

    // a pasted image comes in at whatever size it was taken at, which is far bigger
    // than the note it's in. It's opened full size from the previewer instead.
    ".ql-editor img": {
        maxWidth: htmlImageMax,
        maxHeight: htmlImageMax,
        cursor: "zoom-in",
    },

    // quill parks the placeholder at a fixed offset of its own, so pull it over to
    // where the text it stands in for starts
    ".ql-editor.ql-blank::before": {
        left: "var(--editor-inset)",
        right: "var(--editor-inset)",
    },
    ".ql-editor ol, .ql-editor ul": { paddingLeft: listIndent },
    ...indentLevels,

    ".ql-snow .ql-stroke": { stroke: "#616161" },
    ".ql-snow .ql-fill": { fill: "#616161" },
    ".ql-snow .ql-picker": { color: "#616161" },
    ".ql-snow .ql-active .ql-stroke": { stroke: "#212121" },
    ".ql-snow .ql-active .ql-fill": { fill: "#212121" },
});
